import React, { useState, useRef, useEffect } from 'react';
import {
  Compass,
  Settings,
  Globe,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Building2,
  Plus,
  Check,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet';
import { useThemeStore } from '@/stores/useThemeStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { useTranslation } from '@/hooks/useTranslation';
import { useLocalizationStore } from '@/stores/useLocalizationStore';
import { useFeatureToggleStore } from '@/stores/useFeatureToggleStore';
import { useNavDiet } from '@/config/navDiet';
import { isItemVisible, type SidebarItem } from '@/config/navigation';
import { useClinicShapeStore } from '@/stores/useClinicShapeStore';
import { MaieLogomark } from '@/components/MaieLogomark';
import {
  buildTenantHandoffUrl,
  type SsoPayload,
} from '@/utils/crossHostSso';
import { fetchTenantConfigForHost } from '@/services/tenantApi';

const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];

// Match the login flow's accent: warm beige used as a quiet highlight.
// Sourced from MAIE.Landing's `maei-beige-500`, exposed as `--color-beige-500`.
const BRAND_BEIGE = 'var(--color-beige-500)';

/**
 * Display label for a UserRole. The enum values themselves are stable
 * because tokens already in flight reference them, so this map is the
 * presentation-layer translation (e.g. value `physician` renders as
 * "Doctor" under the MVP role catalog).
 */
const ROLE_DISPLAY_LABEL: Record<string, string> = {
  owner: 'Owner',
  physician: 'Doctor',
  nurse: 'Nurse',
  receptionist: 'Receptionist',
  billing: 'Billing',
  super_admin: 'Platform admin',
  it_support: 'IT Support',
};

const formatRoleLabel = (role: string | undefined): string => {
  if (!role) return '';
  const mapped = ROLE_DISPLAY_LABEL[role];
  if (mapped) return mapped;
  // Fallback for legacy / not-yet-mapped values.
  return role.replace(/_/g, ' ');
};

export type { SidebarItem } from "@/config/navigation";

interface SidebarProps {
  items: SidebarItem[];
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  isMobileMenuOpen: boolean;
  onMobileMenuClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  items,
  collapsed,
  onCollapsedChange,
  isMobileMenuOpen,
  onMobileMenuClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { branding: rawBranding } = useThemeStore();
  const {
    user,
    logout,
    roleCheck,
    activeTenant,
    activeTenantId,
    availableTenants,
    switchTenant,
    refreshMemberships,
    popLastTokenPayload,
  } = useAuthStore();
  // Hospital-name fallback chain:
  //   1. tenant_brandings.displayName (overlaid into branding by useTheme)
  //   2. activeTenant.displayName from auth (always present post-auth)
  //   3. The legacy 'MAIE' platform default
  // Without (2), tenants whose branding row never got a displayName
  // saved would forever show "MAIE" in the sidebar even though their
  // tenant entity has a real name. Treat the bare platform default as
  // "no real value yet" and prefer the auth-side tenant name.
  const branding = (() => {
    const isPlatformDefault =
      !rawBranding.hospitalName || rawBranding.hospitalName === 'MAIE';
    if (isPlatformDefault && activeTenant?.displayName) {
      return { ...rawBranding, hospitalName: activeTenant.displayName };
    }
    return rawBranding;
  })();
  const { hasPermission, hasRole, hasAnyPermission } = roleCheck;
  const { t } = useTranslation();
  const { setLanguage, getCurrentLanguage } = useLocalizationStore();
  const { isModuleEnabled } = useFeatureToggleStore();
  const clinicShape = useClinicShapeStore((s) => s.shape);
  // Explore only leaves the sidebar under the diet; until then it is
  // still a row, and listing it twice would just be clutter.
  const { navDiet } = useNavDiet();
  // Pre-diet, sections collapse. Everything starts open — half-shut groups
  // hid whole features from doctors — but the chevrons existed and removing
  // them for tenants who are not on the diet would be an unasked-for change.
  // The diet's sections are labels, so this is never read there.
  const [expandedSections, setExpandedSections] = useState<string[]>([
    'schedule',
    'clinical',
    'library',
    'inbound',
    'admin',
  ]);
  const toggleSection = (sectionId: string) =>
    setExpandedSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId],
    );
  const showEverything = useClinicShapeStore((s) => s.showEverything);
  // Everything expanded. Half-collapsed groups hid whole features behind a
  // chevron nobody clicked — a doctor can't discover Patient portal or
  // Templates if the group they live in is shut by default. Groups are
  // short now that Administration's patient-facing items moved out, so
  // the wall this guarded against no longer exists; /explore carries
  // discovery, and the sidebar just has to not hide things.
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isMobileUserDropdownOpen, setIsMobileUserDropdownOpen] = useState(false);
  const [switchingTenantId, setSwitchingTenantId] = useState<string | null>(null);
  const [hostMode, setHostMode] = useState<'shared' | 'tenant' | 'unknown'>(
    'unknown',
  );
  const [publicHostBase, setPublicHostBase] = useState<string | null>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const mobileUserDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch the tenant memberships once so the dropdown shows other
  // workspaces this user belongs to (the auth payload only carries the
  // active tenant). Also resolve publicHostBase + whether we're on the
  // shared host so "Add another clinic" can route correctly when
  // rendered on a tenant subdomain.
  useEffect(() => {
    if (availableTenants.length === 0) {
      refreshMemberships().catch(() => undefined);
    }
  }, [availableTenants.length, refreshMemberships]);

  useEffect(() => {
    let cancelled = false;
    fetchTenantConfigForHost()
      .then((cfg) => {
        if (cancelled) return;
        setPublicHostBase(cfg.publicHostBase);
        setHostMode(cfg.mode === 'shared' ? 'shared' : 'tenant');
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSwitchTenant = async (tenantId: string) => {
    if (tenantId === activeTenantId || switchingTenantId) return;
    setSwitchingTenantId(tenantId);
    try {
      await switchTenant(tenantId);
      const target = availableTenants.find((t) => t.tenantId === tenantId);
      const currentHost = window.location.host;
      const preferredHost = target?.preferredHost ?? null;
      if (preferredHost && preferredHost !== currentHost && publicHostBase) {
        const payload = popLastTokenPayload();
        if (payload?.tenant?.slug) {
          window.location.replace(
            buildTenantHandoffUrl(
              payload.tenant.slug,
              publicHostBase,
              payload as unknown as SsoPayload,
            ),
          );
          return;
        }
        window.location.href = `${window.location.protocol}//${preferredHost}/login`;
        return;
      }
      window.location.reload();
    } catch (err) {
      console.error('[sidebar] tenant switch failed', err);
      setSwitchingTenantId(null);
    }
  };

  const handleCreateClinic = () => {
    setIsUserDropdownOpen(false);
    setIsMobileUserDropdownOpen(false);
    if (hostMode === 'tenant' && publicHostBase) {
      const protocol = window.location.protocol;
      const port = window.location.port ? `:${window.location.port}` : '';
      window.location.href = `${protocol}//${publicHostBase}${port}/clinics/new`;
      return;
    }
    navigate('/clinics/new');
  };

  // Reusable workspace section for the user dropdown — same markup on
  // desktop and mobile. Renders nothing when the user has no other
  // memberships AND we don't yet know the host mode (avoids a flash).
  const renderWorkspaceSection = (closeDropdown: () => void) => {
    const others = availableTenants.filter(
      (t) => t.tenantId !== activeTenantId,
    );
    if (others.length === 0 && hostMode === 'unknown') return null;
    return (
      <>
        <div
          className="border-t"
          style={{ borderColor: 'var(--color-border-primary)' }}
        />
        {others.length > 0 && (
          <>
            <div
              className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.18em] font-semibold"
              style={{ color: 'var(--color-text-tertiary)' }}
            >
              {isRTL ? 'تبديل العيادة' : 'Switch clinic'}
            </div>
            {others.map((t) => (
              <button
                key={t.tenantId}
                onClick={() => {
                  closeDropdown();
                  void handleSwitchTenant(t.tenantId);
                }}
                disabled={switchingTenantId !== null}
                className={`w-full px-3 py-2.5 text-sm ${isRTL ? 'text-right' : 'text-left'} transition-all duration-200 flex items-center gap-3 disabled:opacity-60`}
                style={{ color: 'var(--color-text-primary)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <Building2 className="w-4 h-4 flex-shrink-0" />
                <span className="truncate flex-1">{t.displayName}</span>
                {t.tenantId === switchingTenantId ? (
                  <span
                    className="text-[10px] uppercase tracking-wider"
                    style={{ color: 'var(--color-text-tertiary)' }}
                  >
                    …
                  </span>
                ) : t.tenantId === activeTenantId ? (
                  <Check
                    className="w-3.5 h-3.5 flex-shrink-0"
                    style={{ color: 'var(--color-success)' }}
                  />
                ) : null}
              </button>
            ))}
          </>
        )}
        <button
          onClick={handleCreateClinic}
          className={`w-full px-3 py-2.5 text-sm ${isRTL ? 'text-right' : 'text-left'} transition-all duration-200 flex items-center gap-3`}
          style={{ color: 'var(--color-text-primary)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <Plus className="w-4 h-4 flex-shrink-0" />
          {isRTL ? 'إضافة عيادة جديدة' : 'Add another clinic'}
        </button>
      </>
    );
  };

  const currentLanguage = getCurrentLanguage();
  const isRTL = RTL_LANGUAGES.includes(currentLanguage);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
      }
      if (mobileUserDropdownRef.current && !mobileUserDropdownRef.current.contains(event.target as Node)) {
        setIsMobileUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const handleNavigation = (path: string) => {
    navigate(path);
    onMobileMenuClose();
  };

  // Visibility lives in src/config/navigation.ts so it can be exercised
  // without mounting React. Permissions come from the live `/auth/me`
  // payload via the auth store, never a static role→permission map.
  const hasAccess = (item: SidebarItem): boolean =>
    isItemVisible(item, {
      hasUser: !!user,
      shape: clinicShape,
      showEverything,
      hasPermission,
      hasAnyPermission,
      hasRole,
      isModuleEnabled,
    });

  // `rail` = desktop collapsed icon-rail mode. It is passed explicitly
  // (desktop passes `collapsed`, the mobile Sheet always passes false) so
  // a persisted collapsed preference can never leak icon-only rendering
  // into the mobile drawer.
  const renderSidebarItem = (item: SidebarItem, level: number = 0, rail: boolean = false) => {
    if (!hasAccess(item)) return null;

    const hasChildren = item.children && item.children.length > 0;
    // Prefix-aware active match — keeps an item highlighted on its deep
    // routes too (e.g. /pharmacy stays active on /pharmacy/refills,
    // /patients stays active on /patients/:id). We require either an
    // exact match or the path followed by a `/` boundary so /pat doesn't
    // light up /patient*.
    const isActive = item.path
      ? location.pathname === item.path ||
        location.pathname.startsWith(`${item.path}/`)
      : false;
    const Icon = item.icon;

    if (hasChildren) {
      // Top-level groupings render as a quiet eyebrow row (uppercase, tracked,
      // beige tick) — matches the login flow's section-label idiom. Nested
      // groupings keep the standard expander button.
      const isTopLevelGroup = level === 0;

      // Icon rail: section headers reduce to a thin divider; the group's
      // children render directly as centered icon buttons (each carries a
      // title tooltip), so every destination stays one click away.
      if (isTopLevelGroup && rail) {
        return (
          <div key={item.id} className="mt-3 first:mt-0">
            <div
              aria-hidden
              className="mx-2 mb-1.5 h-px"
              style={{ backgroundColor: 'var(--color-border-primary)' }}
            />
            <div className="flex flex-col gap-0.5">
              {item.children?.map((child) => renderSidebarItem(child, level + 1, rail))}
            </div>
          </div>
        );
      }

      if (isTopLevelGroup && !rail) {
        const isExpanded = navDiet || expandedSections.includes(item.id);
        const Heading = navDiet ? 'div' : 'button';
        return (
          <div key={item.id} className="mt-4 first:mt-1">
            <Heading
              {...(navDiet
                ? {}
                : {
                    onClick: () => toggleSection(item.id),
                    'aria-expanded': isExpanded,
                  })}
              className="w-full flex items-center gap-2 px-3 pt-1 pb-2 group"
            >
              <span
                aria-hidden
                className="w-3 h-px flex-shrink-0 transition-all duration-200"
                style={{
                  backgroundColor: BRAND_BEIGE,
                  opacity: isExpanded ? 1 : 0.55,
                }}
              />
              <span
                className={`flex-shrink-0 ${isRTL ? 'text-right' : 'text-left'} text-[10px] uppercase tracking-[0.2em] font-semibold whitespace-nowrap transition-colors duration-200`}
                style={{
                  color: isExpanded
                    ? 'var(--color-text-secondary)'
                    : 'var(--color-text-tertiary)',
                }}
              >
                {item.label}
              </span>
              <span
                aria-hidden
                className="flex-1 h-px min-w-[8px] transition-opacity duration-200"
                style={{
                  backgroundColor: 'var(--color-border-primary)',
                  opacity: isExpanded ? 0.6 : 0.4,
                }}
              />
              {!navDiet && (
                <ChevronDown
                  className={`w-3 h-3 transition-transform duration-200 flex-shrink-0 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
                  style={{
                    color: 'var(--color-text-tertiary)',
                    opacity: 0.7,
                  }}
                />
              )}
            </Heading>
            {isExpanded && (
              <div className="flex flex-col gap-0.5 pt-0.5">
                {item.children?.map((child) =>
                  renderSidebarItem(child, level + 1, rail),
                )}
              </div>
            )}
          </div>
        );
      }

      // No third level exists: the tree is section -> destination, and a
      // section is a label rather than a collapsible. If a nested group is
      // ever reintroduced, render it here rather than reviving the expander.
      return null;
    }

    return (
      <button
        key={item.id}
        onClick={() => item.path && handleNavigation(item.path)}
        title={
          rail
            ? typeof item.badge === 'number' && item.badge > 0
              ? `${item.label} (${item.badge > 99 ? '99+' : item.badge})`
              : item.label
            : undefined
        }
        className={`w-full flex items-center gap-3 py-2.5 text-sm rounded-lg transition-all duration-200 group mb-0.5 relative ${rail
          ? 'justify-center px-0'
          : `px-3 ${!navDiet && level > 0 ? (isRTL ? 'mr-3 pr-6' : 'ml-3 pl-6') : ''}`
          }`}
        style={{
          backgroundColor: isActive
            ? 'var(--color-bg-selected)'
            : 'transparent',
          // Derived, scheme-aware selected text — readable on the tint over
          // both light and dark page backgrounds (was a fixed primary-700,
          // which vanished on dark custom themes).
          color: isActive
            ? 'var(--color-text-selected)'
            : 'var(--color-text-secondary)',
          fontWeight: isActive ? 600 : 400,
        }}
        onMouseEnter={(e) => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
            e.currentTarget.style.color = 'var(--color-text-primary)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--color-text-secondary)';
          }
        }}
      >
        {/* Active indicator — primary (indigo) tick on the leading edge.
            Beige is reserved for the eyebrow/section-label accent only;
            interactive states (active, hover, focus) use the primary scale. */}
        {isActive && (
          <span
            aria-hidden
            className="absolute top-1/2 -translate-y-1/2 rounded-full"
            style={{
              [isRTL ? 'right' : 'left']: 0,
              width: 3,
              height: 18,
              backgroundColor: 'var(--color-primary-600)',
            }}
          />
        )}
        <Icon className="w-5 h-5 flex-shrink-0" />
        {/* Rail badge — a small dot in the corner so pending counts stay
            visible even without room for the numeric pill (exact count
            lives in the title tooltip). */}
        {rail && typeof item.badge === 'number' && item.badge > 0 && (
          <span
            aria-hidden
            className="absolute top-1.5 end-2.5 w-2 h-2 rounded-full"
            style={{ backgroundColor: 'var(--color-warning)' }}
          />
        )}
        {!rail && <span className="truncate">{item.label}</span>}
        {!rail && typeof item.badge === 'number' && item.badge > 0 && (
          <span
            className="ms-auto inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full text-[10px] font-semibold tabular-nums"
            style={{
              backgroundColor: 'var(--color-warning)',
              color: '#fff',
            }}
            aria-label={`${item.badge} pending`}
          >
            {item.badge > 99 ? '99+' : item.badge}
          </span>
        )}
      </button>
    );
  };



  return (
    <>
      {/* Desktop Sidebar */}
      <div
        className={`${collapsed ? 'w-16' : 'w-64'
          } bg-white shadow-lg transition-[width] duration-200 flex-col hidden lg:flex`}
        style={{ backgroundColor: 'var(--color-bg-secondary)', borderRight: '1px solid var(--color-border-primary)' }}
      >
        {/* Sidebar Header — quiet, login-aligned: small logomark + thin
            wordmark on light surfaces, beige hairline accent on the leading
            edge to echo the eyebrow idiom used across login/dashboard. */}
        <div
          className="relative flex-shrink-0 flex items-center justify-between gap-2 px-4 py-[14px]"
          style={{ borderBottom: '1px solid var(--color-border-primary)' }}
        >
          {!collapsed ? (
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              {branding.logo ? (
                <img
                  src={branding.logo}
                  alt={branding.hospitalName}
                  className="h-7 w-auto object-contain flex-shrink-0"
                  style={{ maxWidth: 32 }}
                />
              ) : (
                <MaieLogomark
                  variant="symbol-blue"
                  size={22}
                  className="flex-shrink-0"
                />
              )}
              <span
                className="font-semibold text-[15px] truncate tracking-tight"
                style={{ color: 'var(--color-text-primary)' }}
                title={branding.hospitalName}
              >
                {branding.hospitalName}
              </span>
            </div>
          ) : branding.logo ? (
            <img
              src={branding.logo}
              alt={branding.hospitalName}
              className="h-7 w-auto object-contain mx-auto"
              style={{ maxWidth: 32 }}
            />
          ) : (
            <MaieLogomark variant="symbol-blue" size={22} className="mx-auto" autoDark />
          )}
          {!collapsed && (
            <button
              onClick={() => onCollapsedChange(!collapsed)}
              className="p-1.5 rounded-md transition-all duration-200 flex-shrink-0"
              title={isRTL ? 'طيّ القائمة الجانبية' : 'Collapse sidebar'}
              aria-label={isRTL ? 'طيّ القائمة الجانبية' : 'Collapse sidebar'}
              aria-expanded={!collapsed}
              style={{ color: 'var(--color-text-tertiary)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                e.currentTarget.style.color = 'var(--color-text-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--color-text-tertiary)';
              }}
            >
              {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          )}
        </div>

        {/* Expand button when collapsed */}
        {collapsed && (
          <div className="px-2 py-3 border-b" style={{ borderColor: 'var(--color-border-primary)' }}>
            <button
              onClick={() => onCollapsedChange(false)}
              className="w-full p-2 rounded-md transition-all duration-200"
              title={isRTL ? 'توسيع القائمة الجانبية' : 'Expand sidebar'}
              aria-label={isRTL ? 'توسيع القائمة الجانبية' : 'Expand sidebar'}
              aria-expanded={!collapsed}
              style={{ color: 'var(--color-text-tertiary)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                e.currentTarget.style.color = 'var(--color-text-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--color-text-tertiary)';
              }}
            >
              {isRTL ? <ChevronLeft className="w-5 h-5 mx-auto" /> : <ChevronRight className="w-5 h-5 mx-auto" />}
            </button>
          </div>
        )}

        {/* Navigation Items — `min-h-0` lets this flex child shrink below its
            content height so it (and only it) scrolls; without it the nav
            pushes the user section + "Powered by" foot past the viewport,
            making them unreachable. */}
        <nav className={`flex-1 min-h-0 overflow-y-auto py-4 ${collapsed ? 'px-2' : 'px-3'}`}>
          <div className="flex flex-col gap-1">{items.map((item) => renderSidebarItem(item, 0, collapsed))}</div>
        </nav>

        {/* User Section */}
        <div className="flex-shrink-0 border-t px-3 py-3" style={{ borderColor: 'var(--color-border-primary)' }}>
          <div className="relative" ref={userDropdownRef}>
            <button
              onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
              className={`w-full flex items-center gap-3 rounded-lg transition-all duration-200 ${collapsed ? 'justify-center p-1.5' : 'p-2.5'}`}
              title={
                collapsed
                  ? user?.displayName || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()
                  : undefined
              }
              style={{ color: 'var(--color-text-primary)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div
                className="flex items-center justify-center w-9 h-9 rounded-full"
                style={{ backgroundColor: 'var(--color-primary-100)', color: 'var(--color-primary-600)' }}
              >
                <span className="font-semibold text-sm">
                  {(user?.firstName?.[0] || '') + (user?.lastName?.[0] || '')}
                </span>
              </div>
              {!collapsed && (
                <>
                  <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'} min-w-0`}>
                    <p className="font-medium text-sm truncate">
                      {user?.displayName || `${user?.firstName} ${user?.lastName}`}
                    </p>
                    <p className="text-xs truncate capitalize" style={{ color: 'var(--color-text-tertiary)' }}>
                      {user?.primaryRole ? formatRoleLabel(user.primaryRole) : t('common.user') || 'User'}
                    </p>
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ${isUserDropdownOpen ? 'rotate-180' : ''
                      }`}
                  />
                </>
              )}
            </button>

            {isUserDropdownOpen && !collapsed && (
              <div
                className="absolute bottom-full mb-2 w-full rounded-xl shadow-xl border overflow-hidden"
                style={{
                  backgroundColor: 'var(--color-bg-secondary)',
                  borderColor: 'var(--color-border-primary)',
                }}
              >
                {navDiet && <button
                  onClick={() => {
                    handleNavigation('/explore');
                    setIsUserDropdownOpen(false);
                  }}
                  className={`w-full px-3 py-2.5 text-sm ${isRTL ? 'text-right' : 'text-left'} transition-all duration-200 flex items-center gap-3`}
                  style={{ color: 'var(--color-text-primary)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <Compass className="w-4 h-4" />
                  {isRTL ? 'استكشف' : 'Explore MAIE'}
                </button>}

                <button
                  onClick={() => {
                    handleNavigation('/settings');
                    setIsUserDropdownOpen(false);
                  }}
                  className={`w-full px-3 py-2.5 text-sm ${isRTL ? 'text-right' : 'text-left'} transition-all duration-200 flex items-center gap-3`}
                  style={{ color: 'var(--color-text-primary)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <Settings className="w-4 h-4" />
                  {t('nav.settings')}
                </button>

                <button
                  onClick={() => {
                    setLanguage(getCurrentLanguage() === 'en' ? 'ar' : 'en');
                    setIsUserDropdownOpen(false);
                  }}
                  className={`w-full px-3 py-2.5 text-sm ${isRTL ? 'text-right' : 'text-left'} transition-all duration-200 flex items-center gap-3`}
                  style={{ color: 'var(--color-text-primary)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <Globe className="w-4 h-4" />
                  {getCurrentLanguage() === 'en' ? 'العربية' : 'English'}
                </button>

                {renderWorkspaceSection(() => setIsUserDropdownOpen(false))}

                <div className="border-t" style={{ borderColor: 'var(--color-border-primary)' }}></div>

                <button
                  onClick={async () => {
                    try {
                      await logout();
                      setIsUserDropdownOpen(false);
                    } catch (error) {
                      console.error('Logout failed:', error);
                    }
                  }}
                  // Themed tint, not `hover:bg-red-50` — that literal is a
                  // light-mode swatch that rendered as a near-white slab
                  // behind red text on dark themes.
                  className="w-full px-3 py-2.5 text-sm text-start transition-all duration-200 flex items-center"
                  style={{ color: 'var(--color-error)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      'color-mix(in srgb, var(--color-error) 14%, transparent)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {t('common.logout')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Powered by MAIE — flows naturally from the user section, no
            separate backdrop. Reads as part of the sidebar foot, not a
            detached widget. */}
        {!collapsed ? (
          <div className="flex-shrink-0 px-4 pt-3 pb-4 flex flex-col items-center gap-2">
            <div className="flex items-center gap-2 w-full">
              <span
                aria-hidden
                className="flex-1 h-px"
                style={{ backgroundColor: 'var(--color-border-primary)' }}
              />
              <span
                className="text-[9px] uppercase tracking-[0.32em] font-semibold whitespace-nowrap"
                style={{ color: 'var(--color-text-tertiary)' }}
              >
                Powered by
              </span>
              <span
                aria-hidden
                className="flex-1 h-px"
                style={{ backgroundColor: 'var(--color-border-primary)' }}
              />
            </div>
            <MaieLogomark variant="logotype-blue" size={36} autoDark />
          </div>
        ) : (
          <div
            className="flex-shrink-0 pt-3 pb-3 flex flex-col items-center gap-1.5"
            title="Powered by MAIE"
          >
            <span
              aria-hidden
              className="w-6 h-px"
              style={{ backgroundColor: 'var(--color-border-primary)' }}
            />
            <MaieLogomark variant="symbol-blue" size={26} autoDark />
          </div>
        )}
      </div>

      {/* Mobile Menu */}
      {/* Mobile drawer — Radix Sheet under the hood (focus-trap, Esc,
          aria-modal). The previous bespoke div had none of those, and
          its backdrop swallowed Esc + Tab inside the open drawer. */}
      <Sheet
        open={isMobileMenuOpen}
        onOpenChange={(open) => {
          if (!open) onMobileMenuClose();
        }}
      >
        <SheetContent
          side="left"
          className="lg:hidden w-80 max-w-full p-0 flex flex-col gap-0"
          style={{ backgroundColor: 'var(--color-bg-secondary)' }}
        >
            <div
              className="sticky top-0 p-4 border-b flex items-center justify-between gap-3"
              style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-secondary)' }}
            >
              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                {branding.logo ? (
                  <img
                    src={branding.logo}
                    alt={branding.hospitalName}
                    className="h-7 w-auto object-contain flex-shrink-0"
                    style={{ maxWidth: 32 }}
                  />
                ) : (
                  <MaieLogomark
                    variant="symbol-blue"
                    size={22}
                    className="flex-shrink-0"
                  />
                )}
                <SheetTitle
                  className="text-base font-semibold tracking-tight truncate"
                  style={{ color: 'var(--color-text-primary)' }}
                  title={branding.hospitalName}
                >
                  {branding.hospitalName}
                </SheetTitle>
              </div>
              {/* Note: Sheet renders its own absolutely-positioned close X
                  in the top corner; we drop the bespoke one to avoid two
                  X buttons stacking on small screens. */}
            </div>

            <div className="flex-1 overflow-y-auto p-4">{items.map((item) => renderSidebarItem(item))}</div>

            {/* Mobile User Section */}
            <div className="border-t p-3" style={{ borderColor: 'var(--color-border-primary)' }}>
              <div className="relative" ref={mobileUserDropdownRef}>
                <button
                  onClick={() => setIsMobileUserDropdownOpen(!isMobileUserDropdownOpen)}
                  className="w-full flex items-center gap-3 p-2.5 rounded-lg transition-all duration-200"
                  style={{ color: 'var(--color-text-primary)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <div
                    className="flex items-center justify-center w-9 h-9 rounded-full"
                    style={{ backgroundColor: 'var(--color-primary-100)', color: 'var(--color-primary-600)' }}
                  >
                    <span className="font-semibold text-sm">
                      {(user?.firstName?.[0] || '') + (user?.lastName?.[0] || '')}
                    </span>
                  </div>
                  <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'} min-w-0`}>
                    <p className="font-medium text-sm truncate">
                      {user?.displayName || `${user?.firstName} ${user?.lastName}`}
                    </p>
                    <p className="text-xs truncate capitalize" style={{ color: 'var(--color-text-tertiary)' }}>
                      {user?.primaryRole ? formatRoleLabel(user.primaryRole) : t('common.user') || 'User'}
                    </p>
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ${isMobileUserDropdownOpen ? 'rotate-180' : ''
                      }`}
                  />
                </button>

                {isMobileUserDropdownOpen && (
                  <div
                    className="absolute bottom-full mb-2 w-full rounded-xl shadow-xl border overflow-hidden"
                    style={{
                      backgroundColor: 'var(--color-bg-secondary)',
                      borderColor: 'var(--color-border-primary)',
                    }}
                  >
                    {navDiet && <button
                      onClick={() => {
                        handleNavigation('/explore');
                        setIsMobileUserDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-2.5 text-sm ${isRTL ? 'text-right' : 'text-left'} transition-all duration-200 flex items-center gap-3`}
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      <Compass className="w-4 h-4" />
                      {isRTL ? 'استكشف' : 'Explore MAIE'}
                    </button>}

                    <button
                      onClick={() => {
                        handleNavigation('/settings');
                        setIsMobileUserDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-2.5 text-sm ${isRTL ? 'text-right' : 'text-left'} transition-all duration-200 flex items-center gap-3`}
                      style={{ color: 'var(--color-text-primary)' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <Settings className="w-4 h-4" />
                      {t('nav.settings')}
                    </button>

                    <button
                      onClick={() => {
                        setLanguage(getCurrentLanguage() === 'en' ? 'ar' : 'en');
                        setIsMobileUserDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-2.5 text-sm ${isRTL ? 'text-right' : 'text-left'} transition-all duration-200 flex items-center gap-3`}
                      style={{ color: 'var(--color-text-primary)' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <Globe className="w-4 h-4" />
                      {getCurrentLanguage() === 'en' ? 'العربية' : 'English'}
                    </button>

                    {renderWorkspaceSection(() => setIsMobileUserDropdownOpen(false))}

                    <div className="border-t" style={{ borderColor: 'var(--color-border-primary)' }}></div>

                    <button
                      onClick={async () => {
                        try {
                          await logout();
                          setIsMobileUserDropdownOpen(false);
                          onMobileMenuClose();
                        } catch (error) {
                          console.error('Logout failed:', error);
                        }
                      }}
                      className="w-full px-3 py-2.5 text-sm text-start transition-all duration-200 flex items-center"
                      style={{ color: 'var(--color-error)' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                          'color-mix(in srgb, var(--color-error) 14%, transparent)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      {t('common.logout')}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Powered by MAIE — flows from user section in mobile drawer. */}
            <div className="px-4 pt-3 pb-4 flex flex-col items-center gap-2">
              <div className="flex items-center gap-2 w-full">
                <span
                  aria-hidden
                  className="flex-1 h-px"
                  style={{ backgroundColor: 'var(--color-border-primary)' }}
                />
                <span
                  className="text-[9px] uppercase tracking-[0.32em] font-semibold whitespace-nowrap"
                  style={{ color: 'var(--color-text-tertiary)' }}
                >
                  Powered by
                </span>
                <span
                  aria-hidden
                  className="flex-1 h-px"
                  style={{ backgroundColor: 'var(--color-border-primary)' }}
                />
              </div>
              <MaieLogomark variant="logotype-blue" size={36} autoDark />
            </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
